<template>
  <div class="flex flex-col gap-6">
    <form class="flex flex-col gap-4" novalidate @submit="$event => onFormSubmit($event)">
      <div class="flex flex-col gap-1.5">
        <ui-label for="login-email">{{ tr('E-mail') }}</ui-label>
        <ui-input
          id="login-email"
          v-model="email"
          type="email"
          autocomplete="username"
          :placeholder="tr('you@gmail.com')"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <ui-label for="login-password">{{ tr('Password') }}</ui-label>
        <password-field
          id="login-password"
          v-model="password"
          :placeholder="tr('Enter at least 8 characters...')"
        />
      </div>

      <ui-button
        type="submit"
        size="lg"
        class="mt-2 w-full"
        :disabled="isLoading"
      >
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        {{ tr('Login') }}
      </ui-button>
    </form>

    <div class="flex flex-col gap-2">
      <router-link to="/auth/forgotten-password" class="text-sm font-semibold text-foreground underline decoration-muted-foreground/50 underline-offset-4 hover:text-primary">
        {{ tr('Forgot password?') }}
      </router-link>
      <router-link to="/auth/signup" class="text-sm font-semibold text-foreground underline decoration-muted-foreground/50 underline-offset-4 hover:text-primary">
        {{ tr('Don\'t have an account? Try it now for 7 days for free!') }}
      </router-link>
    </div>
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import { Login2faNecessaryError, UnauthorizedError } from '@/errors';
import Google2faCodeForm from '@/ui/components/Google2faCodeForm.vue';
import PasswordField from '@/ui/components/PasswordField.vue';
import { isValidEmail } from '@/utils/validators';

export default {
  name: 'LoginView',
  components: {
    LoaderCircleIcon: LoaderCircle,
    PasswordField,
  },
  data() {
    return {
      isLoading: false,
      email: '',
      password: '',
      code: '',
    };
  },
  methods: {
    resetForm() {
      this.email = '';
      this.password = '';
      this.code = '';
    },
    open2faFullscreenModal() {
      if (this.fullscreenModalInstance) {
        this.$fullscreenModal.closeAll();
      }

      this.fullscreenModalInstance = this.$fullscreenModal.open(
        this,
        Google2faCodeForm,
        () => ({
          modelValue: this.code,
          title: this.tr('Two-factor authentication'),
          trustOption: true,
          isLoading: this.isLoading,
        }),
        {
          componentListeners: {
            submit: ({ trust }) => this.onFormSubmit(null, trust),
            cancel: () => this.$fullscreenModal.closeAll(),
            'update:modelValue': newCode => this.code = newCode,
          },
        },
      );
    },

    onFormSubmit($event, trust) {
      if ($event) {
        $event.preventDefault();
      }

      if (!this.validate()) {
        return;
      }

      this.isLoading = true;
      this.$store
        .dispatch('auth/login', {
          email: this.email,
          password: this.password,
          code: this.code,
          trust: trust ?? false,
        })
        .then(resp => {
          if (window.PasswordCredential) {
            const passwordCredential = new window.PasswordCredential({ id: this.email, password: this.password });
            navigator.credentials.store(passwordCredential);
          }

          this.$store.commit('app/language', this.$root.loggedInUser.language);

          this.$fullscreenModal.closeAll();
          this.$router.replace({ path: '/' });
          this.$nextTick(() => this.resetForm());

          return resp;
        })
        .catch(err => {
          if (err instanceof Login2faNecessaryError) {
            this.open2faFullscreenModal();
          }
          else if (err instanceof UnauthorizedError) {
            this.$toast.error(this.tr('Invalid login information.'));
          }
          else {
            this.error(err);
          }
        })
        .finally(() => this.isLoading = false);
    },
    validate() {
      this.$toast.clear();

      if (!this.email) {
        this.$toast.error(this.tr('E-mail field cannot be empty!'));
        return false;
      }
      if (!isValidEmail(this.email)) {
        this.$toast.error(this.tr('E-mail provided has an incorrect format!'));
        return false;
      }
      if (!this.password) {
        this.$toast.error(this.tr('Password field cannot be empty!'));
        return false;
      }
      if (this.password.length < 8) {
        this.$toast.error(this.tr('The password must be at least 8 characters long.'));
        return false;
      }

      return true;
    },
  },
};
</script>
